import React, { useState, useEffect, useMemo } from 'react';
import { useConfirm } from '../context/ConfirmContext';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { Customer, CreateCustomer, UpdateCustomer, CustomerType, Tag, CreateCustomerPersonDto } from '../types';
import { customersAPI, tagsAPI, customerPersonsAPI } from '../services/api';
import CustomersTable from './CustomersTable';
import Pagination from './Pagination';
import MultiSelectDropdown from './MultiSelectDropdown';
import SearchableDropdown from './SearchableDropdown';
import { usePagination } from '../hooks/usePagination';
import { saveFiltersToStorage, loadFiltersFromStorage } from '../utils/filterStorage';
import { navigateWithRedirect } from '../utils/redirectUtils';

const Customers: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | undefined>();
  // Load initial filters from localStorage
  const defaultFilters = { typeFilter: -1, isArchiveFilter: -1, tagFilter: [], groupFilter: '', searchText: '' };
  const initialFilters = loadFiltersFromStorage('customers', defaultFilters);

  const [typeFilter, setTypeFilter] = useState<number>(initialFilters.typeFilter);
  const [isArchiveFilter, setIsArchiveFilter] = useState<number>(initialFilters.isArchiveFilter);
  const [tagFilter, setTagFilter] = useState<number[]>(initialFilters.tagFilter);
  const [groupFilter, setGroupFilter] = useState<string>(initialFilters.groupFilter);
  const [searchText, setSearchText] = useState<string>(initialFilters.searchText);
  const confirm = useConfirm();

  useEffect(() => {
    loadCustomers();
    loadTags();
  }, []);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const data = await customersAPI.getCustomers();
      setCustomers(data);
      setError(null);
    } catch (err) {
      setError('Failed to load customers');
      console.error('Error loading customers:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadTags = async () => {
    try {
      const data = await tagsAPI.getTags();
      setTags(data);
    } catch (err) {
      console.error('Error loading tags:', err);
    }
  };

  const handleCreateCustomer = async (customerData: CreateCustomer, pendingPersons?: CreateCustomerPersonDto[]) => {
    try {
      setFormLoading(true);
      const createdCustomer = await customersAPI.createCustomer(customerData);

      if (pendingPersons && pendingPersons.length > 0) {
        await Promise.all(
          pendingPersons.map(cp =>
            customerPersonsAPI.createCustomerPerson({
              ...cp,
              customerId: createdCustomer.id
            })
          )
        );
      }

      await loadCustomers();
      setShowForm(false);
      setEditingCustomer(undefined);
      // Navigate back to redirect URL or default to customers list
      navigateWithRedirect(navigate, '/customers', searchParams);
    } catch (err) {
      setError('Failed to create customer');
      console.error('Error creating customer:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateCustomer = async (customerData: UpdateCustomer) => {
    try {
      setFormLoading(true);
      await customersAPI.updateCustomer(customerData.id, customerData);
      await loadCustomers();
      setShowForm(false);
      setEditingCustomer(undefined);
      // Navigate back to redirect URL or default to customers list
      navigateWithRedirect(navigate, '/customers', searchParams);
    } catch (err) {
      setError('Failed to update customer');
      console.error('Error updating customer:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteCustomer = async (id: number) => {
    const isConfirmed = await confirm({
      title: 'Delete customer',
      message: 'Are you sure you want to delete this customer? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger'
    });
    if (!isConfirmed) return;

    try {
      await customersAPI.deleteCustomer(id);
      await loadCustomers();
    } catch (err) {
      setError('Failed to delete customer');
      console.error('Error deleting customer:', err);
    }
  };

  const handleEditCustomer = (customer: Customer) => {
    // Navigate to edit URL with redirect parameter to return to current location
    navigate(`/customers/edit/${customer.id}?redirect=/customers`);
  };

  const handleViewDetails = (id: number) => {
    // Navigate to customer details page
    navigate(`/customers/${id}`);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingCustomer(undefined);
    // Navigate back to redirect URL or default to customers list
    navigateWithRedirect(navigate, '/customers', searchParams);
  };

  const handleFormSubmit = async (customerData: CreateCustomer | UpdateCustomer, pendingPersons?: CreateCustomerPersonDto[]) => {
    if ('id' in customerData) {
      await handleUpdateCustomer(customerData);
    } else {
      await handleCreateCustomer(customerData, pendingPersons);
    }
  };

  const handleNewTagCreated = (newTag: Tag) => {
    // Add the new tag to the tags list used for filtering
    setTags(prev => [...prev, newTag]);
  };

  const handleTagFilterChange = (newTagFilter: number[]) => {
    setTagFilter(newTagFilter);
    saveFiltersToStorage('customers', { typeFilter, isArchiveFilter, tagFilter: newTagFilter, groupFilter, searchText });
  };

  const groupItems = useMemo(() => {
    const groups = Array.from(
      new Set(
        customers
          .map(customer => customer.group?.trim())
          .filter((group): group is string => Boolean(group))
      )
    ).sort((a, b) => a.localeCompare(b));

    return [{ id: '', title: 'All Groups' }, ...groups.map(group => ({ id: group, title: group }))];
  }, [customers]);

  const filteredCustomers = customers.filter(customer => {
    const matchesType = typeFilter === -1 || customer.type === typeFilter;
    const matchesArchive =
      isArchiveFilter === -1 || (isArchiveFilter === 1 ? customer.isArchive : !customer.isArchive);
    const matchesTags = tagFilter.length === 0 || tagFilter.some(tagId => customer.tags?.some(tag => tag.id === tagId));
    const matchesGroup =
      !groupFilter || (customer.group ?? '').toLowerCase() === groupFilter.toLowerCase();
    const matchesSearch = !searchText ||
      customer.title.toLowerCase().includes(searchText.toLowerCase()) ||
      (customer.group ?? '').toLowerCase().includes(searchText.toLowerCase()) ||
      customer.description?.toLowerCase().includes(searchText.toLowerCase()) ||
      customer.website?.toLowerCase().includes(searchText.toLowerCase());

    return matchesType && matchesArchive && matchesTags && matchesGroup && matchesSearch;
  });

  // Sort: active first, archived last; then alphabetical by title
  const sortedCustomers = [...filteredCustomers].sort((a, b) => {
    if (a.isArchive !== b.isArchive) return a.isArchive ? 1 : -1;
    return a.title.localeCompare(b.title);
  });

  const {
    currentPage,
    pageSize,
    paginatedData: paginatedCustomers,
    totalItems,
    goToPage,
    setPageSize
  } = usePagination({ data: sortedCustomers, initialPageSize: 10 });

  return (
    <div className="fade-in">
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h1 style={{ 
              color: 'var(--text-primary)', 
              fontSize: 'var(--font-size-3xl)', 
              fontWeight: '700',
              marginBottom: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              Customers
            </h1>
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-lg)', margin: 0 }}>
              Manage your customer database
            </p>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/customers/create?redirect=/customers')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            Create Customer
          </button>
        </div>
      </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {loading ? (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              minHeight: '50vh',
              flexDirection: 'column',
              gap: '1rem'
            }}>
              <div className="spinner-border" role="status"></div>
              <p style={{ color: 'var(--text-muted)' }}>Loading customers...</p>
            </div>
          ) : (
            <div className="card">
              <div className="card-header">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <h5 className="card-title">Customer List</h5>
                  <div className="ms-auto d-flex flex-wrap gap-2 align-items-end" style={{ justifyContent: 'flex-end' }}>
                    <div>
                      <input
                        type="text"
                        id="customerSearchText"
                        className="form-control"
                        placeholder="Search customers..."
                        value={searchText}
                        onChange={(e) => {
                          const newValue = e.target.value;
                            setSearchText(newValue);
                            saveFiltersToStorage('customers', { typeFilter, isArchiveFilter, tagFilter, groupFilter, searchText: newValue });
                          }}
                        />
                      </div>
                    <div>
                      <select
                        id="customerTypeFilter"
                        className="form-select"
                        value={typeFilter}
                        onChange={(e) => {
                          const newValue = parseInt(e.target.value);
                          setTypeFilter(newValue);
                          saveFiltersToStorage('customers', { typeFilter: newValue, isArchiveFilter, tagFilter, groupFilter, searchText });
                        }}
                      >
                        {[
                          { value: -1, label: 'All Types' },
                          { value: CustomerType.Government, label: 'Government' },
                          { value: CustomerType.Education, label: 'Education' },
                          { value: CustomerType.Legal, label: 'Legal' },
                          { value: CustomerType.Healthcare, label: 'Healthcare' },
                          { value: CustomerType.Retail, label: 'Retail' },
                          { value: CustomerType.Banking, label: 'Banking' },
                          { value: CustomerType.Nonprofit, label: 'Nonprofit' },
                          { value: CustomerType.Publishers, label: 'Publishers' },
                          { value: CustomerType.Agriculture, label: 'Agriculture' },
                          { value: CustomerType.Logistics, label: 'Logistics' },
                          { value: CustomerType.Telecommunications, label: 'Telecommunications' },
                          { value: CustomerType.Media, label: 'Media' },
                          { value: CustomerType.Hospitality, label: 'Hospitality' },
                          { value: CustomerType.ITCluster, label: 'IT Cluster' },
                        ].map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <select
                        id="customerArchiveFilter"
                        className="form-select"
                        value={isArchiveFilter}
                        onChange={(e) => {
                          const newValue = parseInt(e.target.value);
                          setIsArchiveFilter(newValue);
                          saveFiltersToStorage('customers', { typeFilter, isArchiveFilter: newValue, tagFilter, groupFilter, searchText });
                        }}
                      >
                        {[
                          { value: -1, label: 'All Activities' },
                          { value: 1, label: 'Archived' },
                          { value: 0, label: 'Active' },
                        ].map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={{ minWidth: '200px' }}>
                      <SearchableDropdown
                        id="customerGroupFilter"
                        label="Group"
                        showLabel={false}
                        placeholder="All Groups"
                        items={groupItems}
                        value={groupFilter}
                        onChange={(value) => {
                          const newValue = String(value);
                          setGroupFilter(newValue);
                          saveFiltersToStorage('customers', { typeFilter, isArchiveFilter, tagFilter, groupFilter: newValue, searchText });
                        }}
                        getItemValue={(item) => item.id}
                        getItemLabel={(item) => item.title}
                        searchPlaceholder="Search groups..."
                        noItemsMessage="No groups found"
                        className="filter-dropdown"
                        size="md"
                      />
                    </div>
                    <div style={{ minWidth: '200px' }}>
                      <MultiSelectDropdown
                        availableTags={tags}
                        selectedTagIds={tagFilter}
                        onSelectionChange={handleTagFilterChange}
                        placeholder="No tags selected"
                        showSelectedTags={false}
                        buttonStyle={{ minWidth: '200px' }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-body">
                <>
                  <CustomersTable
                    customers={paginatedCustomers}
                    tags={tags}
                    typeFilter={typeFilter}
                    isArchiveFilter={isArchiveFilter}
                    tagFilter={tagFilter}
                    searchText={searchText}
                    onSearchTextChange={(newValue: string) => {
                      setSearchText(newValue);
                      saveFiltersToStorage('customers', { typeFilter, isArchiveFilter, tagFilter, groupFilter, searchText: newValue });
                    }}
                    onTypeFilterChange={(newValue: number) => {
                      setTypeFilter(newValue);
                      saveFiltersToStorage('customers', { typeFilter: newValue, isArchiveFilter, tagFilter, groupFilter, searchText });
                    }}
                    onIsArchiveFilterChange={(newValue: number) => {
                      setIsArchiveFilter(newValue);
                      saveFiltersToStorage('customers', { typeFilter, isArchiveFilter: newValue, tagFilter, groupFilter, searchText });
                    }}
                    onTagFilterChange={handleTagFilterChange}
                    onEdit={handleEditCustomer}
                    onDelete={handleDeleteCustomer}
                    onViewDetails={handleViewDetails}
                  />
                  {totalItems === 0 ? (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '2rem 1rem 0',
                      color: 'var(--text-muted)'
                    }}>
                      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
                        <i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
                      </div>
                      <h5>No customers found</h5>
                      <p>Try adjusting your search filters or create a new customer.</p>
                    </div>
                  ) : (
                    <Pagination
                      currentPage={currentPage}
                      totalItems={totalItems}
                      itemsPerPage={pageSize}
                      onPageChange={goToPage}
                      onPageSizeChange={setPageSize}
                      pageSizeOptions={[10, 25, 50, 100]}
                    />
                  )}
                </>
              </div>
            </div>
          )}
    </div>
  );
};

export default Customers;
